import * as React from 'react';
import { isfunc } from 'piral-base';
import { __RouterContext } from 'react-router';
import { PiralError, PiralLoadingIndicator, ErrorBoundary, PortalRenderer } from '../components';
import { useActions, useGlobalStateContext } from '../hooks';
import { defaultRender, convertComponent, none } from '../utils';
// this is an arbitrary start number to have 6 digits
let portalIdBase = 123456;
const DefaultWrapper = (props) => defaultRender(props.children);
class ForeignComponentContainer extends React.Component {
    constructor() {
        super(...arguments);
        this.handler = (ev) => {
            const { innerProps } = this.props;
            ev.stopPropagation();
            innerProps.piral.renderHtmlExtension(ev.detail.target, ev.detail.props);
        };
        this.setNode = (node) => {
            this.current = node;
        };
    }
    componentDidMount() {
        const node = this.current;
        const { $component, $context, innerProps } = this.props;
        const { mount } = $component;
        if (node && isfunc(mount)) {
            mount(node, innerProps, $context);
            node.addEventListener('render-html', this.handler, false);
        }
        this.previous = node;
    }
    componentDidUpdate() {
        const { current, previous } = this;
        const { $component, $context, innerProps } = this.props;
        const { update } = $component;
        if (current !== previous) {
            previous && this.componentWillUnmount();
            current && this.componentDidMount();
        }
        else if (isfunc(update)) {
            update(current, innerProps, $context);
        }
    }
    componentWillUnmount() {
        const node = this.previous;
        const { $component } = this.props;
        const { unmount } = $component;
        if (node && isfunc(unmount)) {
            unmount(node);
            node.removeEventListener('render-html', this.handler, false);
        }
        this.previous = undefined;
    }
    render() {
        const { $portalId } = this.props;
        return React.createElement("div", { "data-portal-id": $portalId, ref: this.setNode });
    }
}
function wrapReactComponent(Component, stasisOptions, piral, Wrapper) {
    return (props) => (React.createElement(Wrapper, Object.assign({}, props, { piral: piral }),
        React.createElement(ErrorBoundary, Object.assign({}, stasisOptions, { renderProps: props }),
            React.createElement(Component, Object.assign({}, props, { piral: piral })))));
}
function wrapForeignComponent(component, stasisOptions, piral, Wrapper) {
    return React.memo((props) => {
        const { destroyPortal } = useActions();
        const { state } = useGlobalStateContext();
        const router = React.useContext(__RouterContext);
        const id = React.useMemo(() => (portalIdBase++).toString(26), none);
        const context = React.useMemo(() => ({ router, state }), [router, state]);
        const innerProps = React.useMemo(() => (Object.assign(Object.assign({}, props), { piral })), [props]);
        React.useEffect(() => () => destroyPortal(id), none);
        return (React.createElement(Wrapper, Object.assign({}, innerProps),
            React.createElement(ErrorBoundary, Object.assign({}, stasisOptions, { renderProps: props }),
                React.createElement(PortalRenderer, { id: id }),
                React.createElement(ForeignComponentContainer, { innerProps: innerProps, "$portalId": id, "$component": component, "$context": context }))));
    });
}
function isNotExotic(component) {
    return !component.$$typeof;
}
function wrapComponent(converters, component, piral, Wrapper, stasisOptions) {
    if (!component) {
        console.error('The given value is not a valid component.');
        // tslint:disable-next-line:no-null-keyword
        component = () => null;
    }
    if (typeof component === 'object' && isNotExotic(component)) {
        const result = convertComponent(converters[component.type], component);
        return wrapForeignComponent(result, stasisOptions, piral, Wrapper);
    }
    return wrapReactComponent(component, stasisOptions, piral, Wrapper);
}
function getWrapper(wrappers, wrapperType) {
    return wrappers[wrapperType] || wrappers['*'] || DefaultWrapper;
}
export function withApi(context, component, piral, errorType, wrapperType = errorType) {
    const converters = context.converters;
    const Wrapper = context.readState((m) => getWrapper(m.registry.wrappers, wrapperType));
    return wrapComponent(converters, component, piral, Wrapper, {
        onError(error) {
            console.error(piral, error);
        },
        renderChild(child) {
            return React.createElement(React.Suspense, { fallback: React.createElement(PiralLoadingIndicator, null) }, child);
        },
        renderError(error, props) {
            return React.createElement(PiralError, Object.assign({ type: errorType, error: error }, props));
        },
    });
}
//# sourceMappingURL=withApi.js.map